import { memo, type MutableRefObject } from 'react';

import { datapointIndicatorLayout } from './XYChartExplore.sty.js';
import { Canvas } from '../../../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../../../core/components/canvas/CanvasContext.js';
import type { CanvasShape } from '../../../../core/components/canvas/types/canvas-shape.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import { DataPointCanvas } from '../../../../core/components/data-point/DataPointCanvas.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { useDotSeriesData } from '../../../hooks/useDotSeriesData.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { useContentState } from '../../../store/selectors.js';
import { DOT_ID, DOT_VARIANT } from '../../../types/xy-chart-internals.js';
import { getCurrentScale } from '../../../utils/get-scale.js';
import { xAccessor, yAccessor } from '../../displays/dot-accessors.js';

/**
 * Component for rendering the dot datapoints on the Y axis based on an X coordinate.
 */
export const XYChartDotPoint = memo(() => {
  const { series, type, datapoint } = useContentState();
  const { width, height } = useGridPlotSize();
  const { seriesToMetadata: dotSeries } = useDotSeriesData();
  const axisIdToScales = useXYChartScales();

  if (!series || !datapoint || type !== DOT_VARIANT) {
    return;
  }

  const { color, xAxis, yAxis, shape } = safeGet(dotSeries, series);

  const xScale = getCurrentScale(axisIdToScales, xAxis.id);
  const yScale = getCurrentScale(axisIdToScales, yAxis.id);

  const dotPoint = {
    color,
    id: DOT_ID,
    x: xAccessor(xScale)(datapoint) + OVERLAY_PADDING,
    y: yAccessor(yScale)(datapoint) + OVERLAY_PADDING,
  };

  return (
    <div className={datapointIndicatorLayout}>
      <CanvasContext.Provider
        value={{
          shapeRefs: new Map<string, MutableRefObject<CanvasShape>>(),
        }}
      >
        <Canvas
          width={width + OVERLAY_PADDING * 2}
          height={height + OVERLAY_PADDING * 2}
        >
          <DataPointCanvas points={[dotPoint]} shape={shape} />
        </Canvas>
      </CanvasContext.Provider>
    </div>
  );
});

XYChartDotPoint['displayName'] = 'XYChartDotPoint';
